/**
 * StatusMultiSelect.tsx — the Leads status filter: a labelled disclosure button
 * that opens a panel of checkboxes in two groups (Active / Closed), with the
 * selected statuses shown as removable chips. Empty selection = the default
 * active scope (placeholder text). Native checkboxes keep it keyboard-operable
 * (Tab / Space); Escape closes the panel and returns focus to the button
 * (WCAG 2.1.1, 4.1.2).
 */

import { useId, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadStatus } from '../../api/types';
import { FilterChips } from '../../components/FilterChips';

export const ACTIVE_STATUSES: readonly LeadStatus[] = [
  'new',
  'pulled',
  'contacted',
  'engaged',
  'collecting',
  'docs_complete',
  'handed_off',
  'consent_unverified',
  'quarantine',
];
export const CLOSED_STATUSES: readonly LeadStatus[] = [
  'closed_known',
  'closed_unknown',
  'opted_out',
  'undeliverable',
];

export interface StatusMultiSelectProps {
  value: readonly LeadStatus[];
  onChange: (next: LeadStatus[]) => void;
}

export const StatusMultiSelect = ({ value, onChange }: StatusMultiSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const uid = useId();
  const panelId = `${uid}-panel`;

  const toggle = (status: LeadStatus) =>
    onChange(value.includes(status) ? value.filter((s) => s !== status) : [...value, status]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    }
  };
  // Focus leaving the whole control closes the panel.
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!rootRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
  };

  const group = (legend: string, statuses: readonly LeadStatus[], testid: string) => (
    <fieldset className="flex flex-col gap-1" data-testid={testid}>
      <legend className="text-meta uppercase tracking-wide text-ink-3">{legend}</legend>
      {statuses.map((status) => (
        <label
          key={status}
          htmlFor={`leads-filter-status-${status}`}
          className="flex items-center gap-1.5 text-body text-ink-1 max-md:min-h-6"
        >
          <input
            type="checkbox"
            id={`leads-filter-status-${status}`}
            name="status"
            value={status}
            checked={value.includes(status)}
            onChange={() => toggle(status)}
            className="h-4 w-4 rounded border-ink-3 bg-surface-1 text-gold focus:ring-outline-focus"
          />
          {t(`leadStatus.${status}`)}
        </label>
      ))}
    </fieldset>
  );

  const chips = value.map((status) => {
    const label = t(`leadStatus.${status}`);
    return {
      key: status,
      label,
      removeLabel: t('filters.remove', { label }),
      onRemove: () => toggle(status),
    };
  });

  return (
    <div className="flex flex-col gap-2" data-testid="leads-status-filter">
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- delegates Escape/blur from its interactive children */}
      <div
        ref={rootRef}
        role="group"
        aria-labelledby={`${uid}-label`}
        className="relative"
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      >
        <span
          id={`${uid}-label`}
          className="mb-1 block text-meta uppercase tracking-wide text-ink-3"
        >
          {t('leads.filters.status')}
        </span>
        <button
          ref={buttonRef}
          type="button"
          data-testid="leads-status-trigger"
          aria-labelledby={`${uid}-label ${uid}-value`}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
          className="inline-flex w-full items-center justify-between gap-2 rounded-lg bg-surface-1 px-3 py-1.5 text-left text-body text-ink-1 ring-1 ring-inset ring-ink-3/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus max-md:min-h-11 sm:w-72"
        >
          <span id={`${uid}-value`} className={value.length === 0 ? 'text-ink-3' : ''}>
            {value.length === 0
              ? t('leads.filters.statusPlaceholder')
              : t('leads.filters.statusSelected', { count: value.length })}
          </span>
          <span aria-hidden="true">{open ? '▴' : '▾'}</span>
        </button>
        {open ? (
          <div
            id={panelId}
            data-testid="leads-status-panel"
            className="absolute z-20 mt-1 flex max-h-80 w-full flex-col gap-3 overflow-auto rounded-lg bg-surface-2 p-3 ring-1 ring-inset ring-ink-3/20 sm:w-72"
          >
            {group(
              t('leads.filters.statusGroupActive'),
              ACTIVE_STATUSES,
              'leads-status-group-active',
            )}
            {group(
              t('leads.filters.statusGroupClosed'),
              CLOSED_STATUSES,
              'leads-status-group-closed',
            )}
          </div>
        ) : null}
      </div>
      <FilterChips chips={chips} testid="leads-status-chips" />
    </div>
  );
};
